import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Office365Dataset } from 'app/model/dataset/office365.dataset';
import { office365UserScopes } from 'app/registries/consts/metadata-consts';
import { TabularTypeDatasetAdapter } from './tabular-type.dataset.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Office365Dataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            structure: '*',
            linkedServiceName: '*',
            parameters: '*',
            type: '*',
            typeProperties: {
                tableName: '*',
                userScopeFilterUri: '*',
                allowedGroups: '*',
                dateFilterColumn: '*',
                startTime: '*',
                endTime: '*',
                predicate: '*'
            }
        }
    }
})
export class Office365DatasetAdapter extends TabularTypeDatasetAdapter<Office365Dataset, Model.DatasetResource> {
    protected async _toResource(dataset: Office365Dataset): Promise<Model.DatasetResource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.Office365Dataset;
        modelDataset.type = Metamodel.modelFor(Office365Dataset).name;
        if (dataset.isV2) {
            modelDataset.schema = this.columnValueToResource(dataset, dataset.columns);
        } else {
            modelDataset.structure = this.columnValueToResource(dataset, dataset.columns);
        }
        modelDataset.typeProperties = {
            tableName: ValueAdapter.toResource(dataset, Office365Dataset.TABLE_NAME),
            predicate: ValueAdapter.toResource(dataset, Office365Dataset.PREDICATE),
            dateFilterColumn: ValueAdapter.toResource(dataset, Office365Dataset.DATEFILTERCOLUMN),
            startTime: ValueAdapter.toResource(dataset, Office365Dataset.STARTTIME),
            endTime: ValueAdapter.toResource(dataset, Office365Dataset.ENDTIME),
            allowedGroups: dataset.userScope === office365UserScopes.group ? ValueAdapter.toResource(dataset, Office365Dataset.ALLOWEDGROUPS) : undefined,
            userScopeFilterUri: dataset.userScope === office365UserScopes.all ? ValueAdapter.toResource(dataset, Office365Dataset.USERSCOPEFILTERURI) : undefined
        };
        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetResource, dataset?: Office365Dataset): Promise<Office365Dataset> {
        if (!dataset) {
            dataset = Office365Dataset.MODEL.create<Office365Dataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        const model = modelDatasetResource.properties as Model.Office365Dataset;
        if (model) {
            if (model.typeProperties) {
                ValueAdapter.toModel(dataset, Office365Dataset.TABLE_NAME, model.typeProperties.tableName);
                ValueAdapter.toModel(dataset, Office365Dataset.PREDICATE, model.typeProperties.predicate);
                ValueAdapter.toModel(dataset, Office365Dataset.DATEFILTERCOLUMN, model.typeProperties.dateFilterColumn);
                ValueAdapter.toModel(dataset, Office365Dataset.STARTTIME, model.typeProperties.startTime);
                ValueAdapter.toModel(dataset, Office365Dataset.ENDTIME, model.typeProperties.endTime);
                ValueAdapter.toModel(dataset, Office365Dataset.ALLOWEDGROUPS, model.typeProperties.allowedGroups);
                ValueAdapter.toModel(dataset, Office365Dataset.USERSCOPEFILTERURI, model.typeProperties.userScopeFilterUri);
                dataset.userScope = (model.typeProperties.allowedGroups) ? office365UserScopes.group : office365UserScopes.all;
            }
            dataset.isV2 = model.schema != null;
            dataset.columns = this.columnResourceToValue(dataset, dataset.isV2 ? model.schema : model.structure);
        }

        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, Office365Dataset.MODEL));
        return dataset;
    }
}
